// ---------------------------------------------------------------
// App lock via Face ID / Touch ID (with device-passcode fallback).
// Native only; the web app is never locked. Fails OPEN — if biometry isn't
// available or the plugin can't load, the app is NOT locked out.
//
// We reach the native plugin through Capacitor's registry rather than importing
// the plugin's JS package — its ESM ships extensionless imports that CRA/webpack
// refuses to bundle. registerPlugin() gives us the native bridge with no bundling.
// ---------------------------------------------------------------
import { registerPlugin } from '@capacitor/core';

const LOCK_KEY = 'ta-app-lock';

const IS_NATIVE = typeof window !== 'undefined'
  && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

// Off by default — the user opts in with the toggle. (Defaulting ON could gate
// the whole app on a device where biometrics aren't set up.)
export function isAppLockEnabled() {
  try {
    return localStorage.getItem(LOCK_KEY) === '1';
  } catch { return false; }
}
export function setAppLockEnabled(on) {
  try { localStorage.setItem(LOCK_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

let cached;
function bio() {
  if (cached !== undefined) return cached;
  try { cached = registerPlugin('BiometricAuth'); } catch { cached = null; }
  return cached;
}

// Race a promise against a timeout so a missing/unresponsive native plugin can
// never hang the lock screen (which would look like a blank/frozen app).
function withTimeout(promise, ms, fallback) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

// Is Face ID / Touch ID actually set up on this device?
export async function biometryAvailable() {
  if (!IS_NATIVE) return false;
  const p = bio();
  if (!p) return false;
  try {
    const info = await withTimeout(Promise.resolve(p.checkBiometry()), 2500, null);
    return !!(info && info.isAvailable);
  } catch { return false; }
}

// Prompt for Face ID / Touch ID. Resolves true on success, false if the user
// cancels or it fails. Falls back to the device passcode.
export async function unlockWithBiometrics(reason = 'Unlock TagAlong') {
  if (!IS_NATIVE) return true;
  const p = bio();
  if (!p) return true; // no plugin → don't lock the user out
  try {
    const ok = await withTimeout(
      Promise.resolve(p.authenticate({
        reason,
        cancelTitle: 'Cancel',
        allowDeviceCredential: true,     // fall back to passcode
        iosFallbackTitle: 'Use Passcode',
        androidTitle: 'Unlock TagAlong',
        androidSubtitle: 'Confirm it’s you',
      })).then(() => true),
      30000,
      false,
    );
    return ok;
  } catch {
    return false;
  }
}
